import React from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSleepSchedule, ScheduleItem } from './SleepScheduleContext';
import { GitCompare, CheckCircle, XCircle, Clock } from 'lucide-react';

export function ComparisonSummary() {
  const { scheduleItems } = useSleepSchedule();

  const hasActualData = scheduleItems.some(item => item.actualStartTime);

  if (!hasActualData) {
    return (
      <Card className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
        <div className="flex items-center gap-3">
          <GitCompare className="h-5 w-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-700">No Activity Yet</p>
            <p className="text-xs text-gray-500">Actual sleep times will appear here once parents log sessions</p>
          </div>
        </div>
      </Card>
    );
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatMinutes = (minutes: number): string => {
    if (minutes === 0) return 'on time';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
  };

  const getItemComparison = (item: ScheduleItem) => {
    const plannedTime = formatTime(item.scheduledStartTime);
    const planned = new Date(item.scheduledStartTime);
    const now = new Date();
    
    // Check if this is a future/upcoming item
    if (planned > now && !item.actualStartTime) {
      return {
        icon: <Clock className="h-4 w-4 text-gray-400" />,
        badge: <Badge variant="outline" className="border-gray-300 text-gray-600">Upcoming</Badge>,
        description: `Scheduled for ${plannedTime}. Not yet time for this activity.`
      };
    }
    
    // Check status first - if it's marked as missed, treat it as not recorded
    if (item.status === 'missed' || !item.actualStartTime) {
      const itemType = item.type === 'nap' ? 'nap' : item.type === 'bedtime' ? 'bedtime' : 'wake time';
      return {
        icon: <Clock className="h-4 w-4 text-gray-400" />,
        badge: <Badge variant="outline" className="border-gray-300 text-gray-600">Not logged</Badge>,
        description: `Scheduled ${itemType} at ${plannedTime} - no data logged yet. Log this activity to track schedule adherence.`
      };
    }

    const actual = new Date(item.actualStartTime);
    const diffMinutes = Math.round((actual.getTime() - planned.getTime()) / (1000 * 60));
    const actualTime = formatTime(item.actualStartTime);

    if (Math.abs(diffMinutes) <= 30) {
      return {
        icon: <CheckCircle className="h-4 w-4 text-green-500" />,
        badge: <Badge className="bg-green-100 text-green-700 border-green-300">On time ✓</Badge>,
        description: `Started at ${actualTime}, within expected range of planned time (${plannedTime}). Good schedule adherence!`
      };
    } else if (diffMinutes > 30) {
      return {
        icon: <Clock className="h-4 w-4 text-yellow-500" />,
        badge: <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">{formatMinutes(diffMinutes)} late</Badge>,
        description: `Was supposed to start at ${plannedTime} but actually started at ${actualTime}. This was ${formatMinutes(diffMinutes)} later than planned.`
      };
    } else {
      return {
        icon: <Clock className="h-4 w-4 text-blue-500" />,
        badge: <Badge className="bg-blue-100 text-blue-700 border-blue-300">{formatMinutes(Math.abs(diffMinutes))} early</Badge>,
        description: `Was planned for ${plannedTime} but started at ${actualTime}. This was ${formatMinutes(Math.abs(diffMinutes))} earlier than scheduled.`
      };
    }
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
      <div className="flex items-center gap-2 mb-4">
        <GitCompare className="h-5 w-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-indigo-900">Schedule Comparison</h3>
      </div>
      <p className="text-xs text-indigo-600 mb-4">
        Comparing planned schedule with actual logged sessions from database (sleep_sessions & nap_sessions tables). 
        Pediatric standard: ±30 minutes is considered on-time.
      </p>

      <div className="space-y-3">
        {scheduleItems.map((item) => {
          const comparison = getItemComparison(item);
          
          return (
            <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-indigo-200">
              <div className="flex items-center gap-3">
                {comparison.icon}
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">{item.label}</p>
                  <p className="text-xs text-gray-600 leading-relaxed mt-1">{comparison.description}</p>
                </div>
              </div>
              {comparison.badge}
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-indigo-200">
        <p className="text-xs text-indigo-700 italic mb-2">
          💡 Tip: Consistent adherence to the schedule helps establish healthy sleep patterns and improves overall sleep quality.
        </p>
        <p className="text-xs text-indigo-600 mt-2">
          ℹ️ Data sourced from sleep_sessions and nap_sessions tables, compared against your custom schedule
        </p>
      </div>
    </Card>
  );
}
